/**
 * publishProgressContent.tsx — PLAT-10947
 *
 * Shared presentation for the two publish-progress surfaces, so they can never
 * drift apart:
 *
 *  - `PublishProgressDrawer` — the floating top-right widget, shown whenever no
 *    header host is mounted (i.e. outside the clip-preview modal).
 *  - `PublishProgressHeaderStrip` — the same content laid into the clip-preview
 *    modal's top-right header band, left of the modal's close (X).
 *
 * Both read the same `PublishTrackingStore` state via `usePublishProgressView`,
 * show the same status copy and counts, and share one `PublishVariantList` for
 * the per-clip, per-language rows with their Share Live Link + Download
 * actions.
 *
 * Their headers deliberately differ, because the containers do:
 *  - `PublishProgressHeader` (drawer) — two rows: title + counts + percent above
 *    a full-width bar, plus expand and dismiss controls. Reads as a card, and
 *    owns the expandable `PublishVariantList`.
 *  - `PublishProgressInlineRow` (strip) — one row: title, counts, and a bar that
 *    absorbs the leftover width. Status only: no percent, no dismiss, nothing to
 *    expand, because the modal around it already supplies all three. Reads as
 *    modal chrome.
 * The pieces they do share (`usePublishProgressView`, `ProgressSummary`, the
 * typography) keep the two from drifting on status wording or scale.
 */

import ProgressBar from '@src/components/common/ProgressBar';
import { DRAWER_TOOLTIP_STYLE } from '@src/components/common/tooltipStyles';
import { useStore } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import ChevronDownIcon from '@src/svgs/ChevronDownIcon';
import CloseIcon from '@src/svgs/CloseIcon';
import DownloadIcon from '@src/svgs/DownloadIcon';
import GlobeExternalIcon from '@src/svgs/GlobeExternalIcon';
import { PublishStatus } from '@src/types.ts/publishRecord';
import { groupBy } from '@src/utility/groupBy';
import { formatLanguageCaption } from '@src/utility/languageCaption';
import { isPublishRecordFinished } from '@src/utility/publishRecord';
import { formatResolutionLabel } from '@src/utility/videoResolution';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

import { findClipForVideoId, shareLanguageForRecord } from './clipLink';
import ClipShareUnlistedLinkModal from './ClipShareUnlistedLinkModal';
import { usePublishProgress } from './hooks/usePublishProgress';
import { usePublishRecordDownload } from './hooks/usePublishRecordDownload';
import { PublishStatusBadge } from './publishStatusUi';

import type { PublishRecord } from '@src/types.ts/publishRecord';

const DISMISS_TOOLTIP_ID = 'publish-progress-dismiss-tooltip';

/**
 * Progress bar width in the in-modal strip. Fixed, so the bar reads the same at
 * every stage instead of growing as the status copy shortens. The strip sizes
 * itself around it — see `STRIP_MAX_WIDTH` in `PublishProgressHeaderStrip`.
 */
const INLINE_BAR_WIDTH = 140;

/** Shown when the story graph carries no title for a published video. */
const UNTITLED_CLIP = 'Untitled clip';

/** One language's rows inside a clip section. */
interface PublishLanguageSection {
  language: string;
  caption: string;
  rows: PublishRecord[];
}

/** One clip's languages — the outer section of the expanded variant list. */
interface PublishClipSection {
  /** Parent clip id where the story graph resolves it, else the record's videoId. */
  clipKey: string;
  title: string;
  languages: PublishLanguageSection[];
}

/** A clip's records grouped by language, sections and rows in stable order. */
function groupByLanguage(records: PublishRecord[]): PublishLanguageSection[] {
  return Array.from(groupBy(records, r => r.language).entries())
    .map(([language, rows]) => ({
      language,
      caption: formatLanguageCaption(language),
      rows: rows.sort(
        (a, b) =>
          a.aspectRatio.localeCompare(b.aspectRatio) ||
          a.resolution.localeCompare(b.resolution),
      ),
    }))
    .sort((a, b) => a.caption.localeCompare(b.caption));
}

/**
 * Watched records grouped clip → language (PLAT-11395). The clip axis is the
 * outer one because the drawer watches *groups*, not a single publish: a second
 * publish in the same session, or several in-flight groups restored by
 * `seedForStory` after a refresh, otherwise render as repeated
 * `AR · resolution` rows with nothing to tell them apart.
 *
 * Keyed on the parent clip id rather than the title, so two clips that happen
 * to share a name stay separate sections. A videoId the story graph doesn't
 * carry (not loaded yet) keys to itself and titles as `UNTITLED_CLIP`.
 */
function groupByClip(
  records: PublishRecord[],
  titleByVideoId: Map<string, string>,
  clipIdByVideoId: Map<string, string>,
): PublishClipSection[] {
  return Array.from(
    groupBy(
      records,
      r => clipIdByVideoId.get(r.videoId) ?? r.videoId,
    ).entries(),
  )
    .map(([clipKey, rows]) => ({
      clipKey,
      title:
        rows.map(r => titleByVideoId.get(r.videoId)).find(Boolean) ??
        UNTITLED_CLIP,
      languages: groupByLanguage(rows),
    }))
    .sort(
      (a, b) =>
        a.title.localeCompare(b.title) || a.clipKey.localeCompare(b.clipKey),
    );
}

export interface PublishProgressView {
  /** Nothing to show — callers render null. */
  isVisible: boolean;
  /** Optimistic window: Publish clicked, the fan-out's records don't exist yet. */
  pending: boolean;
  expanded: boolean;
  progress: number;
  pct: number;
  title: string;
  summary: { finishedCount: number; total: number; failedCount: number };
  /** Watched variants, clip → language → rows. */
  groups: PublishClipSection[];
  /**
   * Scoped hosts only: the still-running groups this scope owns, watched or
   * not. Empty unscoped. The header strip re-watches these so a publish that
   * outlived a drawer dismissal comes back when its clip is reopened.
   */
  liveGroupIds: string[];
  /** videoId → friendly clip title, for download file names. */
  titleByVideoId: Map<string, string>;
}

/**
 * Every derived value both surfaces need. Kept as one hook so the title copy,
 * the counts and the estimated-progress call stay identical between them.
 *
 * `scopeClipId` narrows every figure to one clip's variants (PLAT-11395). The
 * header strip passes the clip its modal has open, because the strip sits in
 * that clip's own header — another clip's publish shown there reads as "this
 * clip is publishing". The floating drawer passes nothing and keeps aggregating
 * every watched group.
 */
export function usePublishProgressView(
  scopeClipId?: string,
): PublishProgressView {
  const { publishTrackingStore } = useStore();
  const { watchedRecords, drawerExpanded, pendingPublishCount } =
    publishTrackingStore;

  // Resolve a friendly download-file base per record from the current story
  // graph. Every watched record belongs to the current story (the store is
  // reset on story switch), so the title lives in `story.otherVideos` — keyed
  // by the clip id and each of its per-AR `associatedVideos` ids (variants
  // don't carry their own title). Built inline, not memoized: as a global
  // watcher this must reflect clips added/removed/renamed on any re-render,
  // and `story` is a shallow `observable.ref` — a `[story]`-keyed memo would go
  // stale on an in-place `otherVideos` change. The list is small, so rebuilding
  // per render is cheap. Missing/unloaded → videoId fallback (in `download`).
  //
  // The same walk collects each variant's rendered-output duration for the
  // progress ETA (PLAT-11209). `clipJson.duration` is the clip's timeline
  // length, re-persisted on every clip save by `ClipVideoCreatorAdapter`, and
  // it comes down with the story query — so it survives a mid-publish refresh.
  // Variants carry their own `clipJson`; they fall back to the parent clip's
  // duration, since an AR variant renders the same timeline length.
  const videoCreator = useVideoCreatorStore();
  const titleByVideoId = new Map<string, string>();
  const durationByVideoId = new Map<string, number>();
  const clipIdByVideoId = new Map<string, string>();
  for (const clip of videoCreator.story?.otherVideos ?? []) {
    const clipDuration = clip.clipJson?.duration;
    if (clip.id) {
      if (clip.title) titleByVideoId.set(clip.id, clip.title);
      if (clipDuration) durationByVideoId.set(clip.id, clipDuration);
      clipIdByVideoId.set(clip.id, clip.id);
    }
    for (const variant of clip.associatedVideos ?? []) {
      if (!variant.id) continue;
      if (clip.title) titleByVideoId.set(variant.id, clip.title);
      const duration = variant.clipJson?.duration ?? clipDuration;
      if (duration) durationByVideoId.set(variant.id, duration);
      // Variants group under their parent clip, so every AR of one clip lands
      // in a single named section (PLAT-11395).
      if (clip.id) clipIdByVideoId.set(variant.id, clip.id);
    }
  }

  // Scoped hosts see only their own clip's variants; unscoped, this is every
  // watched record. Records are keyed on the per-AR variant id, so both sides
  // resolve through `clipIdByVideoId` to compare parent clips.
  const scopeKey = scopeClipId
    ? (clipIdByVideoId.get(scopeClipId) ?? scopeClipId)
    : null;
  const records = scopeKey
    ? watchedRecords.filter(
        r => (clipIdByVideoId.get(r.videoId) ?? r.videoId) === scopeKey,
      )
    : watchedRecords;

  // Still-running groups belonging to this scope, whether or not the drawer is
  // still watching them — dismissing it clears the whole watch set, and the
  // clip's own header strip is entitled to show the clip's own live publish.
  const liveGroupIds = scopeKey
    ? Array.from(
        new Set(
          publishTrackingStore.inFlightRecords
            .filter(
              r => (clipIdByVideoId.get(r.videoId) ?? r.videoId) === scopeKey,
            )
            .map(r => r.groupId),
        ),
      )
    : [];

  // Estimated, monotonic progress interpolated between the real status pushes
  // (the backend exposes no percentage). Per-clip duration feeds each
  // variant's ETA.
  const progress = usePublishProgress(
    records,
    pendingPublishCount,
    durationByVideoId,
  );

  // The optimistic window belongs to whoever is asking: a scoped host only sees
  // it because its own modal dispatched the publish — the Publish buttons live
  // in the open clip's modal and on the clip cards behind it, and the cards are
  // unreachable while the modal is up.
  const pending = records.length === 0 && pendingPublishCount !== null;
  const total = pending ? (pendingPublishCount ?? 0) : records.length;
  const allFinished =
    records.length > 0 && records.every(isPublishRecordFinished);

  return {
    isVisible: pending || records.length > 0,
    pending,
    expanded: drawerExpanded,
    progress,
    pct: Math.round(progress),
    // The ellipsis marks work still in progress, so it rides the two in-flight
    // states and not the terminal one — "Complete…" would be a contradiction.
    title: allFinished
      ? 'Complete'
      : pending
        ? // The count lives in the title while pending — a separate
          // "Preparing N variants…" summary would just repeat it.
          `Preparing ${total} variant${total !== 1 ? 's' : ''}…`
        : 'Publishing…',
    summary: {
      total,
      finishedCount: records.filter(isPublishRecordFinished).length,
      failedCount: records.filter(r => r.publishStatus === PublishStatus.Failed)
        .length,
    },
    groups: groupByClip(records, titleByVideoId, clipIdByVideoId),
    liveGroupIds,
    titleByVideoId,
  };
}

/**
 * The counts + percent run — "1 of 2 · 55%", with "· N failed" spliced in when
 * any variant has failed. Identical in both surfaces: the bar shows progress at
 * a glance, but the exact figures are what people read off, so both carry them.
 */
const ProgressSummary = observer(function ProgressSummary({
  view,
}: {
  view: PublishProgressView;
}) {
  const { summary, pct } = view;
  return (
    <>
      {summary.finishedCount} of {summary.total}
      {summary.failedCount > 0 && (
        <FailedText> · {summary.failedCount} failed</FailedText>
      )}
      {' · '}
      {pct}%
    </>
  );
});

/** Shared expand/collapse caret. */
const ExpandToggle = observer(function ExpandToggle({
  expanded,
  onToggleExpanded,
}: {
  expanded: boolean;
  onToggleExpanded: (expanded: boolean) => void;
}) {
  return (
    <IconButton
      type="button"
      aria-label={expanded ? 'Collapse variant list' : 'Expand variant list'}
      aria-expanded={expanded}
      data-testid="publish-progress-toggle"
      onClick={() => onToggleExpanded(!expanded)}
    >
      <Caret $expanded={expanded}>
        <ChevronDownIcon width="12" height="12" strokeColor="currentColor" />
      </Caret>
    </IconButton>
  );
});

/**
 * The floating drawer's two-row header: status title + counts/percent + the
 * expand and dismiss controls, above a full-width progress bar. The in-modal
 * strip uses `PublishProgressInlineRow` instead — see that component for why
 * the layouts differ.
 */
export const PublishProgressHeader = observer(function PublishProgressHeader({
  view,
  expanded,
  onToggleExpanded,
}: {
  view: PublishProgressView;
  /**
   * Owned by the caller, not read off the view — the drawer uses the store's
   * `drawerExpanded` (it must survive `RootLayout` remounting per route, and it
   * auto-expands on completion), while the header strip keeps expansion local
   * so a completing publish never throws a panel over the modal's tabs
   * unasked.
   */
  expanded: boolean;
  onToggleExpanded: (expanded: boolean) => void;
}) {
  const { publishTrackingStore } = useStore();
  const { pending, title, progress } = view;

  return (
    <BarBlock>
      <TitleRow>
        <Title>{title}</Title>
        {!pending && (
          <InlineSummary data-testid="publish-progress-summary">
            <ProgressSummary view={view} />
          </InlineSummary>
        )}
        <Controls>
          {!pending && (
            <ExpandToggle
              expanded={expanded}
              onToggleExpanded={onToggleExpanded}
            />
          )}
          {/* Persistent Close — available at every stage of the publish
              lifecycle (PLAT-10796). Collapsing only minimizes progress; Close
              dismisses it so the user can return to the main interface. The
              fan-out keeps running in the background — which the tooltip says
              outright, because an X on a progress widget otherwise reads as
              "cancel" (PLAT-10947). */}
          <IconButton
            type="button"
            aria-label="Hide — this won't cancel publishing"
            data-testid="publish-progress-dismiss"
            data-tooltip-id={DISMISS_TOOLTIP_ID}
            data-tooltip-content="Hide — this won't cancel publishing"
            onClick={publishTrackingStore.dismissDrawer}
          >
            <CloseIcon width={12} height={12} />
          </IconButton>
          <Tooltip
            id={DISMISS_TOOLTIP_ID}
            style={DRAWER_TOOLTIP_STYLE}
            place="bottom"
            positionStrategy="fixed"
            noArrow
          />
        </Controls>
      </TitleRow>

      <ProgressBar value={progress} max={100} height={3} />
    </BarBlock>
  );
});

/**
 * The in-modal strip's layout: one line — status, counts + percent, then the
 * progress bar filling the remaining width. Read-only status; nothing to expand
 * or dismiss.
 *
 * It deliberately diverges from the drawer's stacked header. The drawer is a
 * card, so a title row above a full-width bar reads correctly there; inside the
 * modal's header band the same two-row block read as a card wedged into the
 * chrome. One line sits in the band the way the surrounding header does.
 *
 * Pared back (PLAT-10947) to no dismiss X — the modal's own close button is
 * inches away — and no expand caret, since the Downloads tab immediately below
 * is the variant list and a panel here would have duplicated it while covering
 * it. The counts and percent stay: they're the figures people actually read.
 * See `PublishProgressHeaderStrip` for the full rationale.
 */
export const PublishProgressInlineRow = observer(
  function PublishProgressInlineRow({ view }: { view: PublishProgressView }) {
    const { pending, title, progress } = view;

    return (
      <InlineRow>
        <Title>{title}</Title>
        {!pending && (
          <InlineMeta data-testid="publish-progress-summary">
            <ProgressSummary view={view} />
          </InlineMeta>
        )}
        {/* Fixed width rather than filling the leftover space: a bar that
            resized with the status text made the header feel unsettled as the
            copy changed between stages. The strip sizes itself to fit. */}
        <ProgressBar
          value={progress}
          max={100}
          height={3}
          width={INLINE_BAR_WIDTH}
        />
      </InlineRow>
    );
  },
);

/**
 * Variant rows grouped clip → language: the clip's name, then each locale, then
 * `AR · resolution` with a live status badge — swapped for the Share Live Link +
 * Download action pair once a variant succeeds, mirroring the Downloads tab.
 * Owns the per-row Share Live Link modal so both surfaces get it for free.
 */
export const PublishVariantList = observer(function PublishVariantList({
  view,
}: {
  view: PublishProgressView;
}) {
  const videoCreator = useVideoCreatorStore();
  const { downloadingId, download } = usePublishRecordDownload();
  const [shareRecord, setShareRecord] = useState<PublishRecord | null>(null);

  return (
    <>
      <VariantScroll data-testid="publish-progress-variants">
        {view.groups.map(clipSection => (
          <ClipSection key={clipSection.clipKey}>
            {/* The clip's name, so a drawer watching more than one publish
                isn't a stack of identical `AR · resolution` rows. `title` for
                the full name — the drawer is 280px wide, so long clip names
                ellipsize (PLAT-11395). */}
            <ClipHeader
              data-testid="publish-progress-clip-name"
              title={clipSection.title}
            >
              {clipSection.title}
            </ClipHeader>
            {clipSection.languages.map(group => (
              <LanguageSection key={group.language}>
                <LanguageHeader>{group.caption}</LanguageHeader>
                {group.rows.map(record => (
                  <VariantRow key={record.id}>
                    <VariantLeft>
                      <VariantMeta>
                        {record.aspectRatio} ·{' '}
                        {formatResolutionLabel(record.resolution)}
                      </VariantMeta>
                      {record.errorMessage && (
                        <ErrorText title={record.errorMessage}>
                          {record.errorMessage}
                        </ErrorText>
                      )}
                    </VariantLeft>
                    {record.publishStatus === PublishStatus.Succeeded ? (
                      <RowActions>
                        <RowIconButton
                          type="button"
                          data-testid="publish-progress-live-link"
                          title="Share Live Link"
                          aria-label="Share Live Link"
                          onClick={() => setShareRecord(record)}
                        >
                          <GlobeExternalIcon
                            width="14"
                            height="14"
                            strokeColor="currentColor"
                          />
                        </RowIconButton>
                        <RowIconButton
                          type="button"
                          data-testid="publish-progress-download"
                          disabled={downloadingId === record.id}
                          title={
                            downloadingId === record.id
                              ? 'Downloading…'
                              : 'Download'
                          }
                          aria-label={
                            downloadingId === record.id
                              ? 'Downloading…'
                              : 'Download'
                          }
                          onClick={() =>
                            download(
                              record,
                              view.titleByVideoId.get(record.videoId),
                            )
                          }
                        >
                          <DownloadIcon
                            width="14"
                            height="14"
                            strokeColor="currentColor"
                          />
                        </RowIconButton>
                      </RowActions>
                    ) : (
                      <PublishStatusBadge status={record.publishStatus} />
                    )}
                  </VariantRow>
                ))}
              </LanguageSection>
            ))}
          </ClipSection>
        ))}
      </VariantScroll>

      {shareRecord &&
        (() => {
          const { clip, mainClip } = findClipForVideoId(
            videoCreator.story?.otherVideos,
            shareRecord.videoId,
          );
          return (
            <ClipShareUnlistedLinkModal
              clip={clip}
              mainClip={mainClip}
              dimension={shareRecord.aspectRatio}
              language={shareLanguageForRecord(
                shareRecord.language,
                videoCreator.story?.originalVideoLanguage,
              )}
              onClose={() => setShareRecord(null)}
              backdropZIndex={SHARE_MODAL_BACKDROP_Z}
            />
          );
        })()}
    </>
  );
});

/* Above both hosts: the floating drawer (1000035) and the header strip's
   anchored panel (1000011, inside a 1000005 modal). */
export const SHARE_MODAL_BACKDROP_Z = 1000036;

// ---------------------------------------------------------------------------
// Styled components — the ExportMenu panel family, condensed for a widget.
// ---------------------------------------------------------------------------

const BarBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-2xs);
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-xs);
`;

/* The strip's single line: fixed-width text, then a flexible bar, then the
   caret. Nothing wraps and nothing stacks, so the row's height stays inside the
   modal's header band. */
const InlineRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  min-width: 0;
`;

/* Counts + percent sitting immediately after the title (not right-aligned like
   the drawer's, where it has a full row to itself). This is the one part
   allowed to shrink: on the longest line ("Complete 12 of 12 · 3 failed ·
   100%") the strip hits its max width, and ellipsizing the figures is far
   better than clipping the fixed-width bar off the right edge. */
const InlineMeta = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  color: var(--text-muted);
  font-variant-numeric: tabular-nums;
`;

const Title = styled.h2`
  margin: 0;
  flex-shrink: 0;
  font-family: var(--font-primary);
  font-size: 13px;
  font-weight: var(--font-weight-semi-bold);
  color: var(--foreground);
`;

// One-line counts + percent, right-aligned between the title and the window
// controls. Ellipsizes in the (rare) extreme "N of M · K failed · pct%" case
// instead of wrapping the single-row header.
const InlineSummary = styled.span`
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: right;
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  color: var(--text-muted);
  font-variant-numeric: tabular-nums;
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border: none;
  border-radius: var(--radius-button, 6px);
  background: transparent;
  color: var(--text-secondary);
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    background: var(--surface-elevated);
    color: var(--foreground);
  }
`;

const Caret = styled.span<{ $expanded: boolean }>`
  display: inline-flex;
  transition: transform var(--transition-fast);
  /* Points down while collapsed (the list opens downward), up when expanded. */
  transform: rotate(${({ $expanded }) => ($expanded ? '180deg' : '0deg')});
`;

const FailedText = styled.span`
  color: var(--destructive);
`;

const VariantScroll = styled.div`
  max-height: 320px;
  overflow-y: auto;
  margin-top: var(--spacing-2xs);
`;

/* Clip sections carry the wider gap; the language sections nested inside them
   keep the tighter one, so the two levels read apart without indentation —
   which would only cost the narrow rows width. */
const ClipSection = styled.div`
  & + & {
    margin-top: var(--spacing-sm);
  }
`;

/* The name of the clip these variants belong to. Hierarchy is by weight and
   colour (semibold `--foreground` over the muted language caption below it),
   not indentation. One line, ellipsized — clip names get long. */
const ClipHeader = styled.div`
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-semi-bold);
  color: var(--foreground);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding-bottom: 2px;
`;

const LanguageSection = styled.div`
  & + & {
    margin-top: var(--spacing-2xs);
  }
`;

const LanguageHeader = styled.div`
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  color: var(--text-muted);
  padding-bottom: 2px;
`;

const VariantRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-sm);
  padding: var(--spacing-2xs) 0;
  border-bottom: 1px solid var(--border);

  &:last-child {
    border-bottom: none;
  }
`;

const VariantLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const VariantMeta = styled.span`
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  color: var(--text-muted);
`;

const ErrorText = styled.span`
  font-family: var(--font-primary);
  font-size: 11px;
  color: var(--destructive);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  /* Fits the drawer's content box (280px − 2×10px padding). Only the drawer
     renders variant rows — the header strip is status-only. */
  max-width: 200px;
`;

// Succeeded-row action pair: Open Live Link (icon) to the left of Download.
const RowActions = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  flex-shrink: 0;
`;

// Shared by both succeeded-row actions (Share Live Link, Download) — icon
// twins of the Downloads tab's action pair, compacted to the row's scale.
// Icon-only (PLAT-10947): a "Download" text link crowded the narrow row, and
// the matched pair of icons reads cleaner at this scale.
const RowIconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  background: none;
  border: none;
  border-radius: var(--radius-button, 6px);
  color: var(--text-muted);
  cursor: pointer;

  &:hover:not(:disabled) {
    color: var(--foreground);
    background: var(--surface-elevated);
  }

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;
